import React from "react";
import { Box, Snackbar, Alert } from "@mui/material";
import { useForm, FormProvider } from "react-hook-form";
import { useParams } from "react-router-dom";
import * as T from "io-ts";

import {
  useUpdateCriteriaMutation,
  useSearchForScholarshipsMutation,
  useGetCriteriaAndResultsQueryQuery,
} from "../api";
import {
  getSensitiveCriteria,
  saveSensitiveCriteria,
  clearSensitiveCriteria,
} from "../util/localStorage";

import { PageContainer } from "../components/PageContainer";
import { Page } from "../components/Page";
import { Button } from "../components/Button";
import { SurveyBody } from "../components/SurveyBody";
import { PageHeader } from "../components/PageHeader";
import { NextButtonText } from "../components/NextButtonText";
import { UnpassableError } from "../components/UnpassableError";
import { Loading } from "../components/Loading";
import { Results } from "./Results";
import { Review } from "./review/Review";
import { Demographics } from "./page-5/Demographics";

import { CurrentSchool } from "./page-2/CurrentSchool";
import { TestScores } from "./page-2/TestScores";
import { Activities } from "./page-2/Activities";
import { Interests } from "./page-2/Interests";
import { GraduationStatus } from "./page-2/GraduationStatus";

import { CollegeReadiness } from "./page-3/CollegeReadiness";
import { CollegeChoices } from "./page-3/CollegeChoices";
import {
  transformFormDataToSchemaData,
  transformSchemaDataToFormData,
  transformSensitiveCriteriaToSchemaData,
} from "../util/transformers";
import { MedicalConditions } from "./page-4/MedicalConditions";
import { Situations } from "./page-4/Situations";
import { Miscellaneous } from "./page-4/Miscellaneous";
import { Affiliations } from "./page-4/Affiliations";
import * as Sentry from "@sentry/browser";
import { RequiredFields } from "./page-1/RequiredFields";
import { Locations } from "./page-1/Locations";

export type TRaceObj = {
  [key: string]: boolean;
};

export function SurveyContainer({ children }: { children: React.ReactNode }) {
  return (
    <PageContainer>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          maxWidth: "46.875rem",
          marginX: { xs: 2, md: "auto" },
          marginTop: "0.938rem",
        }}
      >
        {children}
      </Box>
    </PageContainer>
  );
}

export function SurveyPage() {
  const { id } = useParams();
  if (id === undefined || id === null) {
    console.error("ID is undefined or null");
    Sentry.captureException("ID is undefined or null");
    return (
      <SurveyContainer>
        <UnpassableError subtitle="Unable to fetch data. Try accessing this page using the URL that was texted to you." />
      </SurveyContainer>
    );
  }
  return <Survey id={id} />;
}

function Survey({ id }: { id: string }) {
  Sentry.setContext("Scholarship Record", { id });

  // Load criteria and scholarships from backend
  const { data, loading, error } = useGetCriteriaAndResultsQueryQuery({
    variables: {
      scholarshipRecordId: id,
    },
  });

  if (loading) {
    return (
      <SurveyContainer>
        <Loading />
      </SurveyContainer>
    );
  }
  if (error || data === undefined || data === null) {
    console.error(error);
    Sentry.captureException(error);
    return (
      <SurveyContainer>
        <UnpassableError subtitle="Unable to fetch data. Our team has been notified." />
      </SurveyContainer>
    );
  }
  let { criteria, scholarships } = data;

  // Show scholarships results page if scholarship record has already submitted criteria
  if (scholarships) {
    const { scholarshipDataCollection } = scholarships;
    if (scholarshipDataCollection) {
      return (
        <SurveyContainer>
          <Results scholarships={scholarshipDataCollection} />
        </SurveyContainer>
      );
    }
  }

  // Convert criteria into format which form uses
  if (criteria === undefined || criteria === null) {
    criteria = {};
  }
  const formData: SurveyFormData = {
    ...transformSchemaDataToFormData(criteria),

    // Add empty default values here bc we don't have a transformer to do this for sensitive fields
    sensitive: {
      gender: "",
      sexuality: "",
      race: {},
      ethnicity: "",
      ancestry: [],
      citizenshipStatuses: [],
      condition: [],
    },
  };

  // Load sensitive criteria
  const sensitiveCriteria = getSensitiveCriteria();
  if (sensitiveCriteria !== null) {
    formData.sensitive = sensitiveCriteria;
  }

  return (
    <SurveyContainer>
      <SurveyForm data={formData} scholarshipRecordId={id} />
    </SurveyContainer>
  );
}

type TDemographics = {
  age: string | number;
  gender: string;
  citizenshipStatuses: string[];
  sexuality: string;
  ancestry: string[];
  race: TRaceObj;
  ethnicity: string;
  currentLocation: LocationData;
  permanentLocation: LocationData & { same_as_current: boolean };
};

type LocationData = {
  city: string;
  state: string;
  county: string;
  country: string;
};

type TCollege = {
  schoolName: string;
  ncesCode: string;
  ceebCode: string;
  schoolType: string;
  schoolAddress?: {
    addressLine1: string;
    addressLine2: string;
    city: string;
    stateOrProvince: string;
    zipOrPostalCode: string;
    country: string;
  };
};

type TSchoolAndMajorChoices = {
  majorName: string;
  schoolName: string;
};

type TCollegeInfo = {
  collegeReadinessPrograms: string[];
  currentEnrollments: TCollege[];
  scholarshipPurpose: string;
  enrollmentStatus: string;
  schoolAndMajorChoices: TSchoolAndMajorChoices[];
};

type TAcademicInfo = {
  currentSchool: {
    gradeLevel: string;
    gpa: string | number;
    classRank: string | number;
  };
  testScores: {
    test: string;
    score: string | number;
  }[];
  activities: string[];
  interests: string[];
  graduationStatus: {
    graduated: string;
    graduationType: string;
    graduationDate: string;
    school?: TCollege;
  };
};

type TAffiliation = {
  entityName: string;
  directRelation: string;
  indirectRelation: string;
};

type TAdditionalInfo = {
  affiliations: TAffiliation[];
  condition: string[];
  situation: string[];
  miscellaneous: string[];
};

export const ShapeSensitiveCriteria = T.type({
  gender: T.string,
  sexuality: T.string,
  race: T.record(T.string, T.boolean),
  ethnicity: T.string,
  ancestry: T.array(T.string),
  citizenshipStatuses: T.array(T.string),
  condition: T.array(T.string),
});

export type TSensitiveCriteria = T.TypeOf<typeof ShapeSensitiveCriteria>;

export type SurveyFormData = {
  demographics: TDemographics;
  collegeInfo: TCollegeInfo;
  academicInfo: TAcademicInfo;
  additionalInfo: TAdditionalInfo;
  sensitive: TSensitiveCriteria;
};

const PAGES = ["1", "2", "3", "4", "5", "review"];

function SurveyForm({
  data,
  scholarshipRecordId,
}: {
  data: SurveyFormData;
  scholarshipRecordId: string;
}) {
  const [updateCriteriaMutation, { error: updateCriteriaError }] =
    useUpdateCriteriaMutation();
  const [
    searchForScholarshipsMutation,
    {
      data: results,
      loading: searchScholarshipsLoading,
      error: searchScholarshipsError,
    },
  ] = useSearchForScholarshipsMutation();
  const [currentPage, setCurrentPage] = React.useState("1");
  const [formErrorsSnackbarOpen, setFormErrorsSnackbarOpen] =
    React.useState(false);
  const [updateCriteriaErrorSnackbarOpen, setUpdateCriteriaErrorSnackbarOpen] =
    React.useState(!!updateCriteriaError);

  const loading = searchScholarshipsLoading;
  const error = searchScholarshipsError;

  React.useEffect(() => {
    if (updateCriteriaErrorSnackbarOpen) {
      // The snackbar auto close is broken so...
      setTimeout(() => {
        setUpdateCriteriaErrorSnackbarOpen(false);
      }, 6000);
    }
  }, [updateCriteriaErrorSnackbarOpen, setUpdateCriteriaErrorSnackbarOpen]);

  const methods = useForm<SurveyFormData>({
    defaultValues: data,
  });

  const onFormSubmitIsValid = React.useCallback(
    async (formData: SurveyFormData) => {
      // Update criteria in backend
      const schemaData = transformFormDataToSchemaData(formData);
      await updateCriteriaMutation({
        variables: {
          scholarshipRecordId: scholarshipRecordId,
          input: schemaData,
        },
      });

      // Update sensitive criteria in local storage
      saveSensitiveCriteria(formData.sensitive);
    },
    [updateCriteriaMutation, scholarshipRecordId]
  );

  const onFormSubmitIsError = React.useCallback(
    async (errors: object) => {
      setFormErrorsSnackbarOpen(true);

      // The autoCloseDuration prop on the snackbar doesn't work so...
      setTimeout(() => setFormErrorsSnackbarOpen(false), 6000);
    },
    [setFormErrorsSnackbarOpen]
  );

  const submitCriteria = React.useCallback(
    async (props?: {
      onFormSubmitIsValid?: (data: SurveyFormData) => void;
      onFormSubmitIsError?: () => void;
    }) => {
      methods.handleSubmit(
        (data: SurveyFormData) => {
          if (props?.onFormSubmitIsValid) {
            props.onFormSubmitIsValid(data);
          }

          onFormSubmitIsValid(data);
        },
        (errors: object) => {
          if (props?.onFormSubmitIsError) {
            props.onFormSubmitIsError();
          }
          onFormSubmitIsError(errors);
        }
      )();
    },
    [methods, onFormSubmitIsValid, onFormSubmitIsError]
  );

  const handlePageChange = React.useCallback(
    (currentPage: string, prev = false) => {
      const searchForResults = async () => {
        const { sensitive } = methods.getValues();
        const sensitiveCriteriaSchemaData =
          transformSensitiveCriteriaToSchemaData(sensitive);
        await searchForScholarshipsMutation({
          variables: {
            scholarshipRecordId: scholarshipRecordId,
            sensitiveCriteria: sensitiveCriteriaSchemaData,
          },
        });
        clearSensitiveCriteria();
      };

      // If going to results page
      if (currentPage === "review" && !prev) {
        searchForResults();
        return;
      }

      // Switch to the next page
      const currentPageIndex = PAGES.findIndex((page) => page === currentPage);
      const newPage = prev
        ? PAGES[currentPageIndex - 1]
        : PAGES[currentPageIndex + 1];

      if (prev) {
        // Clicked back button
        setCurrentPage(newPage ? newPage : "1");
      } else {
        // Clicked next button
        submitCriteria({
          onFormSubmitIsValid: (data) => {
            setCurrentPage(newPage ? newPage : "1");
            window.scrollTo(0, 0);
          },
        });
      }
    },
    [
      setCurrentPage,
      submitCriteria,
      searchForScholarshipsMutation,
      scholarshipRecordId,
      methods,
    ]
  );

  if (loading) {
    return <Loading />;
  }

  if (error) {
    console.error(error);
    Sentry.captureException(error);
    return (
      <UnpassableError subtitle="Unable to fetch data. Our team has been notified." />
    );
  }

  if (results?.searchForScholarships.scholarshipDataCollection) {
    return (
      <Results
        scholarships={results?.searchForScholarships.scholarshipDataCollection}
      />
    );
  }

  return (
    <>
      <FormProvider {...methods}>
        <form
          onSubmit={() => submitCriteria()}
          style={{
            // for some unbeknownst reason, the form has 16px of margin at the bottom of it.
            marginBottom: 0, // this cascades over that
          }}
        >
          {Object.keys(methods.formState.errors).length > 0 && (
            <Snackbar
              open={formErrorsSnackbarOpen}
              onClose={() => setFormErrorsSnackbarOpen(false)}
            >
              <Alert
                onClose={() => setFormErrorsSnackbarOpen(false)}
                severity="error"
              >
                Please check your form input.
              </Alert>
            </Snackbar>
          )}

          <Snackbar
            open={updateCriteriaErrorSnackbarOpen}
            onClose={() => setUpdateCriteriaErrorSnackbarOpen(false)}
          >
            <Alert severity="error">
              Failed to save your criteria, please try again later.
            </Alert>
          </Snackbar>

          <Page currentPage={currentPage} page={"1"}>
            <PageHeader
              title="Scholarship Questionnaire"
              body={[
                "We've created this online questionnaire to help you target funding opportunities that match your educational and/or research plans.",
                "To get the best results for funding opportunities, the following questions are required.",
              ]}
            />
            <SurveyBody>
              <RequiredFields />
            </SurveyBody>
          </Page>

          <Page currentPage={currentPage} page={"2"}>
            <PageHeader
              title="Current Status"
              body={[
                "The remainder of this survey is optional. While answering more questions usually means better results, you’re free to share only as much as you want to share!",
              ]}
            />
            <SurveyBody>
              <CurrentSchool />
              <TestScores />
              <Activities />
              <Interests />
              <GraduationStatus />
              <Locations />
            </SurveyBody>
          </Page>

          <Page currentPage={currentPage} page={"3"}>
            <PageHeader title="College Information" />
            <SurveyBody>
              <CollegeReadiness />
              <CollegeChoices />
            </SurveyBody>
          </Page>

          <Page currentPage={currentPage} page={"4"}>
            <PageHeader title="Additional Information" />
            <SurveyBody>
              <Affiliations />
              <Situations />
              <Miscellaneous />
            </SurveyBody>
          </Page>
          <Page currentPage={currentPage} page={"5"}>
            <PageHeader title="Demographics" />
            <SurveyBody>
              <Alert severity="info">
                For your privacy, the answers you submit on this page are not
                stored in our database.
              </Alert>
              <Demographics />
              <MedicalConditions />
            </SurveyBody>
          </Page>
          <Page currentPage={currentPage} page={"review"}>
            <Review setCurrentPage={setCurrentPage} />
          </Page>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              padding: 2,
            }}
          >
            {currentPage !== "1" ? (
              <Button
                id="prev-page-button"
                data-current-page={currentPage}
                onClick={() => handlePageChange(currentPage, true)}
              >
                Back
              </Button>
            ) : (
              <div />
            )}
            <Box>Page {currentPage} of 5</Box>
            <Button
              id="next-page-button"
              data-current-page={currentPage}
              variant="contained"
              onClick={() => handlePageChange(currentPage)}
            >
              <NextButtonText currentPage={currentPage} />
            </Button>
          </Box>
        </form>
      </FormProvider>
    </>
  );
}
